import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, Loader2, Upload, FileText, Euro, Clock, Pencil, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Card, Chip, Button, Label, Input, Textarea } from "../components/Primitives";
import { getRequestByRef, createProposal, myProposalOnRequest, updateProposal } from "../lib/db";

export default function ProposalForm() {
  const { ref: refParam } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ message: "", price: "", days: "", notes: "" });
  const [files, setFiles] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await getRequestByRef(refParam);
        if (!alive) return;
        setReq(r);
        if (r?.dbId && user?.id) {
          const mine = await myProposalOnRequest(user.id, r.dbId);
          if (alive && mine) {
            setExisting(mine);
            setForm({
              message: mine.excerpt || "",
              price: mine.proposed_price != null ? String(mine.proposed_price) : "",
              days: mine.proposed_days != null ? String(mine.proposed_days) : "",
              notes: "",
            });
          }
        }
      } catch (e) { console.error(e); toast.error("Could not load"); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [refParam, user?.id]);

  const isOwner = req && user?.id && req.ownerId === user.id;
  const isFinalized = existing && ["accepted","declined","completed","withdrawn"].includes(existing.status);
  const isEditing = !!existing && !isFinalized;
  const isNotOpen = req && req.status !== "open";

  const validate = () => {
    const e = {};
    if (!form.message.trim()) e.message = t("proposal.errMessage");
    else if (form.message.trim().length < 20) e.message = t("proposal.errMessageShort");
    if (form.price !== "" && (isNaN(Number(form.price)) || Number(form.price) < 0)) e.price = t("proposal.errPrice");
    if (form.days !== "" && (isNaN(Number(form.days)) || Number(form.days) < 0)) e.days = t("proposal.errDays");
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (evt) => {
    evt?.preventDefault?.();
    if (submitting) return;
    if (!user?.id) { toast.error("Please sign in"); return; }
    if (isOwner) { toast.error(t("proposal.ownRequestError")); return; }
    if (isFinalized) { toast.error(t("proposalDetail.finalizedHint")); return; }
    if (isNotOpen) { toast.error(t("proposal.requestNotOpen")); return; }
    if (!validate()) return;
    setSubmitting(true);
    try {
      const notesBlock = form.notes.trim() ? `\n\n— ${form.notes.trim()}` : "";
      const composed = `${form.message.trim()}${notesBlock}`;
      if (isEditing) {
        await updateProposal(existing.id, { message: composed, price: form.price, days: form.days });
        toast.success(t("proposalDetail.updated"), { description: req.id });
        navigate(`/app/proposals/${existing.id}`);
      } else {
        const res = await createProposal(user.id, req.dbId, {
          message: composed, price: form.price, days: form.days,
        }, files);
        toast.success(res.wasUpdate ? t("proposalDetail.updated") : t("proposal.sent"), { description: req.id });
        if (res.application?.id) navigate(`/app/proposals/${res.application.id}`);
        else navigate(`/app/requests/${req.id}`);
      }
    } catch (e) {
      toast.error(e?.message || "Could not send proposal");
    } finally { setSubmitting(false); }
  };

  const onFilePick = (e) => {
    const picked = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...picked]);
  };

  if (loading) return <div className="p-8 max-w-3xl mx-auto text-ink-500 font-mono text-xs">Loading…</div>;
  if (!req) return (
    <div className="p-8 max-w-3xl mx-auto">
      <button onClick={() => navigate("/app/requests")} className="text-ink-600 flex items-center gap-1 mb-4 text-sm hover:text-ink-900">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </button>
      <Card className="p-10 text-center"><p className="text-ink-700 text-sm">{t("requestDetail.notFound")}</p></Card>
    </div>
  );

  if (isOwner) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <button onClick={() => navigate(`/app/requests/${req.id}`)} className="text-ink-600 flex items-center gap-1 mb-4 text-sm hover:text-ink-900">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <Card className="p-8 text-center" data-testid="proposal-own-request-block">
          <p className="text-ink-900 text-sm font-medium">{t("proposal.ownRequestBlockTitle")}</p>
          <p className="text-ink-600 text-xs mt-1">{t("proposal.ownRequestBlockHelp")}</p>
        </Card>
      </div>
    );
  }

  if (isFinalized) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <button onClick={() => navigate(`/app/proposals/${existing.id}`)} className="text-ink-600 flex items-center gap-1 mb-4 text-sm hover:text-ink-900">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <Card className="p-8 text-center" data-testid="proposal-finalized-block">
          <p className="text-ink-900 text-sm font-medium">{t("proposalDetail.finalizedTitle")}</p>
          <p className="text-ink-600 text-xs mt-1">{t("proposalDetail.finalizedHint")}</p>
          <Button className="mt-4" onClick={() => navigate(`/app/proposals/${existing.id}`)} data-testid="proposal-finalized-open">
            {t("proposalDetail.openProposal")}
          </Button>
        </Card>
      </div>
    );
  }

  if (isNotOpen) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <button onClick={() => navigate(`/app/requests/${req.id}`)} className="text-ink-600 flex items-center gap-1 mb-4 text-sm hover:text-ink-900">
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>
        <Card className="p-8 text-center" data-testid="proposal-not-open-block">
          <p className="text-ink-900 text-sm font-medium">{t("proposal.requestNotOpenTitle")}</p>
          <p className="text-ink-600 text-xs mt-1">{t("proposal.requestNotOpenHint")}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto animate-fade-up" data-testid="proposal-form-page">
      <button onClick={() => navigate(`/app/requests/${req.id}`)} data-testid="proposal-back" className="text-ink-600 flex items-center gap-1 mb-4 text-sm hover:text-ink-900">
        <ArrowLeft className="w-4 h-4" /> {t("common.back")}
      </button>

      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-electric-600 mb-2">
        {isEditing ? t("proposalDetail.editKicker") : t("proposal.kicker")}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-ink-900">
          {isEditing ? t("proposalDetail.editTitle") : t("proposal.title")}
        </h1>
        {isEditing && <Chip className="!bg-amber-500/10 !text-amber-500 !border-amber-500/30" data-testid="proposal-edit-mode-badge">{t("proposalDetail.editing")}</Chip>}
      </div>
      <p className="text-ink-600 mt-1 text-sm">
        {isEditing ? t("proposalDetail.editSubtitle") : t("proposal.subtitle")}
      </p>

      <Card className="p-4 my-5 bg-carbon-800/70" data-testid="proposal-request-context">
        <div className="flex items-center gap-3 text-xs">
          <span className="font-mono text-electric-600">{req.id}</span>
          <span className="text-ink-500">·</span>
          <span className="text-ink-800 truncate">{lang === "it" ? req.title : req.titleEn}</span>
        </div>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-ink-600">
          <span className="font-mono">{req.material || "—"}</span>
          <span className="font-mono">{req.budget}</span>
          <span>{req.deadline || "—"}</span>
          <span>{req.location || "—"}</span>
        </div>
      </Card>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label>{t("proposal.messageLabel")} <span className="text-red-400">*</span></Label>
          <Textarea
            value={form.message}
            onChange={(e) => { setForm({ ...form, message: e.target.value }); if (errors.message) setErrors({ ...errors, message: null }); }}
            placeholder={t("proposal.messagePlaceholder")}
            className={errors.message ? "!border-red-500/60" : ""}
            data-testid="proposal-message"
            rows={5}
            autoFocus
          />
          {errors.message && <p className="text-[11px] text-red-400 mt-1" data-testid="err-proposal-message">{errors.message}</p>}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label><Euro className="inline w-3 h-3 mr-1" /> {t("proposal.priceLabel")}</Label>
            <Input
              type="number"
              inputMode="decimal"
              value={form.price}
              onChange={(e) => { setForm({ ...form, price: e.target.value }); if (errors.price) setErrors({ ...errors, price: null }); }}
              placeholder="e.g. 18000"
              className={errors.price ? "!border-red-500/60" : ""}
              data-testid="proposal-price"
            />
            {errors.price && <p className="text-[11px] text-red-400 mt-1">{errors.price}</p>}
          </div>
          <div>
            <Label><Clock className="inline w-3 h-3 mr-1" /> {t("proposal.daysLabel")}</Label>
            <Input
              type="number"
              inputMode="numeric"
              value={form.days}
              onChange={(e) => { setForm({ ...form, days: e.target.value }); if (errors.days) setErrors({ ...errors, days: null }); }}
              placeholder="e.g. 30"
              className={errors.days ? "!border-red-500/60" : ""}
              data-testid="proposal-days"
            />
            {errors.days && <p className="text-[11px] text-red-400 mt-1">{errors.days}</p>}
          </div>
        </div>

        <div>
          <Label>{t("proposal.notesLabel")}</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder={t("proposal.notesPlaceholder")}
            data-testid="proposal-notes"
            rows={2}
          />
        </div>

        <div>
          <Label>{t("proposal.filesLabel")}</Label>
          <label className="block border-2 border-dashed border-carbon-600 rounded-xl p-5 text-center hover:border-electric-500/50 transition cursor-pointer" data-testid="proposal-file-drop">
            <input type="file" multiple onChange={onFilePick} className="hidden" data-testid="proposal-file-input" />
            <Upload className="w-5 h-5 mx-auto text-ink-500 mb-1.5" />
            <div className="text-xs text-ink-600">{t("newRequest.dropFiles")}</div>
          </label>
          {files.length > 0 && (
            <ul className="mt-2 space-y-1">
              {files.map((f, i) => (
                <li key={i} className="text-[11px] font-mono text-ink-700 flex items-center gap-2">
                  <FileText className="w-3 h-3" /> {f.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap pt-2">
          <Button type="submit" disabled={submitting} data-testid="proposal-submit" className="flex-1 sm:flex-initial h-11 px-6">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : isEditing ? <><CheckCheck className="w-4 h-4" /> {t("proposalDetail.saveChanges")}</> : <><Send className="w-4 h-4" /> {t("proposal.submit")}</>}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(`/app/requests/${req.id}`)} disabled={submitting} data-testid="proposal-cancel">
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}
